// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { AppType, ExperienceTypes, PageType, WebWorkerMessageId, environment, logger, perfMarker, sendMessage, webWorkerCache } from "../wwCommon";
import { getExperienceTypeConfig } from "../wwResolver";
import { FetchAppAnonFailed } from "../AppErrors";
import { defaultOneServiceOcid, subscribeToExperienceConfig } from "./Common";
import { get } from "lodash-es";
import { registerDataMessageHandler } from "../wwCommon";
/** Makes fetch call for app anon cookie and marks cache state as fetching item. */
export function registerFetchAppAnonCookie() {
    if (!isFeatureEnabled()) {
        return;
    }
    registerDataMessageHandler(WebWorkerMessageId.AppAnonCookie, () => {
        // Subscribe to the web worker cache, which returns immediately if the response already exists or is added
        const appAnonCookieSubscription = webWorkerCache.subscribe(WebWorkerMessageId.AppAnonCookie, appAnonCookieResponse => {
            appAnonCookieSubscription.unsubscribe();
            sendMessage({ id: WebWorkerMessageId.AppAnonCookie, fetched: true, payload: appAnonCookieResponse.payload });
        });
    });
    fetchAppAnonCookie();
}
function fetchAppAnonCookie() {
    return __awaiter(this, void 0, void 0, function* () {
        const entryPointConfigType = environment.apptype === AppType.EdgeChromium ?
            ExperienceTypes.EntryPointEdgeNext : ExperienceTypes.EntryPoint;
        yield subscribeToExperienceConfig(entryPointConfigType);
        perfMarker("AppAnonFetchStart");
        const entryPointConfig = getExperienceTypeConfig(entryPointConfigType);
        const useOneServiceAuthEndpoints = get(entryPointConfig, "properties.useOneServiceAuthEndpoints");
        let url;
        if (useOneServiceAuthEndpoints) {
            url = `https://api.msn.com/auth/cookie/appanon?scn=app_anon&ocid=${defaultOneServiceOcid}&apikey=${environment.oneServiceApiKey}`;
        }
        else {
            url = `${environment.domain}/spartan/${environment.locale}/getappanoncookie`;
        }
        const init = {
            credentials: "include"
        };
        // add a test hook so AppAnon and aace cookies are not cleared out and refetched
        if (environment && environment.getQueryParameter("isAutomation") === "true") {
            fetchAppAnonCookieComplete(true);
            return Promise.resolve(true);
        }
        if (environment && !environment.signedIn) {
            return fetch(url, init).then((response) => {
                if (!response.ok) {
                    throw Error(`fetchAppAnonCookie call failed: Status: ${response.status} and StatusText: ${response.statusText}`);
                }
                return true;
            }).then(appAnonCookieExists => {
                fetchAppAnonCookieComplete(appAnonCookieExists);
                return true;
            }).catch(reason => {
                logger.error(Object.assign(Object.assign({}, FetchAppAnonFailed), { message: "AppAnon cookie fetch failed", pb: Object.assign(Object.assign({}, FetchAppAnonFailed.pb), { customMessage: `Error: ${reason} for Url: ${url}` }) }));
                fetchAppAnonCookieComplete();
                return false;
            });
        }
        fetch(url, init);
        fetchAppAnonCookieComplete(true);
        return Promise.resolve(true);
    });
}
/** Marks cache state complete for app anon cookie fetch. Responds to any pending message for app anon cookie. */
function fetchAppAnonCookieComplete(appAnonCookieExists = false) {
    perfMarker("AppAnonFetchEnd");
    logger.info(`WebWorker.fetchAppAnonCookieComplete: [id ${WebWorkerMessageId.AppAnonCookie}]`);
    webWorkerCache.set(WebWorkerMessageId.AppAnonCookie, { payload: { appAnonCookieExists } });
}
/** Checks if AppAnonCookie feature is enabled */
function isFeatureEnabled() {
    // Disable the getappanoncookie call on the China ntp
    const locale = environment.locale;
    if (locale === "zh-cn" && location.host.includes(".msn.cn")) {
        return false;
    }
    // Disable the getappanoncookie call on the Enterprise blended page
    if (environment.isEnterpriseCompliantNTP) {
        return false;
    }
    return environment.apptype === AppType.Edge ||
        environment.apptype === AppType.EdgeChromium ||
        environment.apptype === AppType.MMX ||
        environment.pagetype === PageType.NewsBar;
}
//# sourceMappingURL=AppAnonCookie.js.map